import { AfterViewInit, Directive, DoCheck, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { AbstractCheckoutPayment } from '@df/checkout/abstract-checkout-payment.component.def';
import { Adyen } from './adyen.namespace';
import { AdyenService } from './adyen.service';
import { Checkout } from '@df/checkout/checkout.interfaces';

@Directive()
export abstract class AbstractAdyenPaymentComponent<T extends Adyen.IPaymentMethod, D extends Adyen.IComponentOptions>
    extends AbstractCheckoutPayment<T>
    implements OnInit, AfterViewInit, OnDestroy, DoCheck
{
    /**
     * Selector for component container
     * @type {string}
     */
    protected abstract readonly containerSelector: string;

    /**
     * Adyen checkout component id
     * @type  {Adyen.Component}
     */
    protected abstract readonly componentId: Adyen.Component | string;

    /**
     * Adyen checkout component instance
     * @type {Adyen.IComponent | undefined}
     */
    protected component?: Adyen.IComponent;

    /**
     * JQLite object for container where we mount adyen components
     * @type {JQLite}
     */
    protected containerElement?: HTMLElement;

    /**
     * Adyen checkout
     * @type {Adyen.ICheckout}
     */
    protected adyenCheckout?: Adyen.ICheckout;

    protected adyen = inject(AdyenService);

    /**
     * Component container
     * important: you need to put #container on div element in template
     */
    @ViewChild('container')
    protected container: ElementRef<any>;

    /**
     * We overwrite parent ngOnInit, we have to init component in ngAfterViewInit because of 'container' ViewChild
     */
    ngOnInit() {}

    ngAfterViewInit() {
        this.containerElement = this.getContainerElement();
        if (this.containerElement) {
            super.ngOnInit();
        }
    }

    // we have to wait for ngAfterViewInit, ngDoCheck is fired before ngAfterViewInit if adyen payment is preselected
    ngDoCheck() {}

    ngOnDestroy() {
        this.unmount();
        super.ngOnDestroy();
    }

    /**
     * Returns if we want to preload scripts and initiate form on component init
     * @returns {boolean}
     */
    protected preload(): boolean {
        return true;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Payment method methods
    ////////
    ////////////////////////////////////////////

    /**
     * Returns payment method
     * @returns {T}
     */
    get method(): T {
        return this.checkoutFormComponent.paymentMethodByCode(this.methodCode) as T;
    }

    /**
     * Returns if this component method was selected in checkout submission
     * @param {Checkout.ISubmissionData} request
     * @returns {boolean}
     */
    protected isSelectedInRequest(request: Checkout.ISubmissionData): boolean {
        return request.paymentMethod.method === this.methodCode;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Script methods
    ////////
    ////////////////////////////////////////////

    initialize(): void {
        if (!this.containerElement) {
            return;
        }
        super.initialize();
    }

    /**
     * On Js load callback
     */
    protected onScriptLoadSuccess(): void {
        super.onScriptLoadSuccess();
        this.initAdyenCheckout();
    }

    protected getJsAssets(): string[] {
        return [this.adyen.getScriptName(this.isTest)];
    }

    protected getCssAssets(): string[] {
        return [this.adyen.getCssName(this.isTest)];
    }

    protected get isTest(): boolean {
        return !!this.method.isTest;
    }

    ////////////////////////////////////////////
    ////////
    ////////    Container methods
    ////////
    ////////////////////////////////////////////

    /**
     * Clears component container
     */
    protected emptyContainer(): void {
        if (this.containerElement) {
            this.containerElement.innerHTML = '';
        }
    }

    ////////////////////////////////////////////
    ////////
    ////////    Adyen checkout methods
    ////////
    ////////////////////////////////////////////

    /**
     * Initialize adyen checkout
     */
    protected initAdyenCheckout(): void {
        if (!this.adyenCheckout) {
            this.adyenCheckout = this.adyen.getCreateAdyenCheckout(this.getAdyenConfiguration());
        }

        if (this.adyenCheckout && !this.component) {
            this.initComponent();
        }
    }

    /**
     * Configuration object for adyen checkout components
     * @returns {Adyen.IConfiguration}
     */
    protected getAdyenConfiguration(): Adyen.IConfiguration {
        return this.adyen.getAdyenConfiguration(this.method.key, this.isTest);
    }

    ////////////////////////////////////////////
    ////////
    ////////    Components methods
    ////////
    ////////////////////////////////////////////

    /**
     * Initiates adyen component
     */
    protected initComponent(): void {
        this.unmount();
        this.component = this.getComponent();
        if (this.component) {
            this.mount(this.component);
        }
    }

    /**
     * creates Adyen checkout component
     * @returns {Adyen.IComponent | undefined}
     */
    protected getComponent(
        type: string = this.componentId,
        options: Adyen.IComponentOptions | undefined = this.getComponentOptions()
    ): Adyen.IComponent | undefined {
        if (options && this.adyenCheckout) {
            return this.adyenCheckout.create(type, options);
        }
        return undefined;
    }

    /**
     * Returns configuration options object for component
     * @returns {D | undefined}
     */
    protected abstract getComponentOptions(): D | undefined;

    /**
     * Mounts adyen component in container
     * @param {Adyen.IComponent} adyenComponent
     */
    protected mount(adyenComponent: Adyen.IComponent): void {
        if (this.getContainerElement()) {
            adyenComponent.mount(this.containerSelector);
        }
        this.detectChanges();
    }

    /**
     * Removes component
     */
    protected unmount(): void {
        if (this.component) {
            this.component.unmount();
            this.component = undefined;
        }

        this.emptyContainer();
        this.detectChanges();
    }

    /**
     * Returns html element for 3DS component
     * @returns {HTMLElement | undefined}
     */
    protected getContainerElement(): HTMLElement | undefined {
        return this.container && this.container.nativeElement;
    }
}